import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HiOutlineChevronDown } from 'react-icons/hi2';
import { useAuthStore } from '../../stores/authStore';
import { hasMinRole } from '../../utils/rbac';
import { GUIDE_SECTIONS, getGuideSectionForRoute } from './guideContent';
import { useGuideStore } from '../../stores/guideStore';
import styles from './GuidePanel.module.css';

export function GuideTOC() {
  const { t } = useTranslation('guide');
  const { pathname } = useLocation();
  const setActiveArticle = useGuideStore((s) => s.setActiveArticle);
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role ? hasMinRole(user.role, 'tenant_admin') : false;

  const contextSection = getGuideSectionForRoute(pathname);
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const s of GUIDE_SECTIONS) {
      initial[s.id] = s.id === contextSection;
    }
    // Always expand getting-started if no context match
    if (!contextSection) initial['getting-started'] = true;
    return initial;
  });

  useEffect(() => {
    if (contextSection) {
      setExpanded((prev) => ({ ...prev, [contextSection]: true }));
    }
  }, [contextSection]);

  function toggle(sectionId: string) {
    setExpanded((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  }

  return (
    <>
      {GUIDE_SECTIONS.map((section) => {
        if (section.adminOnly && !isAdmin) return null;
        const isExpanded = expanded[section.id] ?? false;
        const Icon = section.icon;
        return (
          <div key={section.id} className={styles.sectionGroup}>
            <button
              className={styles.sectionHeader}
              onClick={() => toggle(section.id)}
            >
              <span className={styles.sectionIcon}><Icon /></span>
              {t(section.titleKey)}
              <span className={`${styles.sectionChevron} ${isExpanded ? styles.sectionChevronOpen : ''}`}>
                <HiOutlineChevronDown />
              </span>
            </button>
            {isExpanded && (
              <div className={styles.sectionArticles}>
                {section.articles.map((article) => (
                  <button
                    key={article.id}
                    className={styles.articleLink}
                    onClick={() => setActiveArticle(article.id)}
                  >
                    {t(article.titleKey)}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}
